/**
 * Exponential backoff for the two boot-time API calls a kiosk tablet cannot
 * afford to lose: the cold-start auto-provision and the SIP-credentials fetch.
 *
 * ── WAAROM ────────────────────────────────────────────────────────────────
 *
 * Beide calls liepen tot 2026-09-23 precies één keer. Faalde de API in die ene
 * seconde na het opstarten (DB in herstel, pool leeg, 503), dan bleef de tablet
 * definitief staan: op /admin-setup, of ingelogd zonder SIP. Op een kiosk vuurt
 * geen resume/visibilitychange, dus er was geen tweede kans tot iemand het
 * toestel fysiek herstartte. Dat is wat ~140 tablets overkwam na de DB-uitval
 * van 2026-09-22/23.
 *
 * De reconnect-ketting van `SipService` heeft al zo'n backoff, maar die start
 * pas NA een geslaagde credentials-fetch. Dit is het stuk ervoor.
 */

export interface BackoffOptions {
  /** First delay. */
  minMs: number;
  /** Ceiling; the delay doubles until it reaches this. */
  maxMs: number;
}

export const BOOT_RETRY: BackoffOptions = { minMs: 5_000, maxMs: 60_000 };

/** Delay before retry number `attempt` (1-based): min·2^(attempt-1), capped at max. */
export function backoffDelay(attempt: number, opts: BackoffOptions = BOOT_RETRY): number {
  const n = Math.max(1, Math.floor(attempt));
  return Math.min(opts.maxMs, opts.minMs * 2 ** (n - 1));
}

export interface RetryTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

const realTimers: RetryTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

/**
 * One retry slot with backoff. `schedule(fn)` arms the next attempt (a no-op
 * while one is already pending), `reset()` cancels it and restarts the
 * backoff from `minMs`. Module-level singletons use this so a retry survives
 * React remounts and stops cleanly on logout.
 */
export function createRetryScheduler(opts: BackoffOptions = BOOT_RETRY, timers: RetryTimers = realTimers) {
  let attempt = 0;
  let handle: unknown = null;

  return {
    /** Arms the next attempt; returns the delay used, or null if one is already pending. */
    schedule(fn: () => void): number | null {
      if (handle !== null) return null;
      attempt += 1;
      const delay = backoffDelay(attempt, opts);
      handle = timers.setTimeout(() => {
        handle = null;
        fn();
      }, delay);
      return delay;
    },
    /** Cancels a pending attempt and resets the backoff. */
    reset(): void {
      if (handle !== null) {
        timers.clearTimeout(handle);
        handle = null;
      }
      attempt = 0;
    },
    get attempt(): number {
      return attempt;
    },
    get pending(): boolean {
      return handle !== null;
    },
  };
}
